/** BOOK — the MFD/RM's whole client base. Dashboard, Clients, Firm. */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { GET_PORTFOLIOS, GET_PORTFOLIO_BOOK_ROW } from '../queries';
import { baseRiskRow } from '../../lib/portfolio';
import { inr, pct, daysAgo, label } from '../../lib/format';
import { EMPTY, sum, byDesc, isClientBook, mapLimited } from './_shared';
import { useClientScope } from './firm';

/**
 * The client book, read one client at a time.
 *
 * `getPortfolios(allUsers: true)` — the single call this hook used to make, and
 * the call every roll-up in the app was built on — returns NOTHING to an
 * advisor. The resolver only honours `allUsers` for a caller with staff
 * employee context; for anyone else it silently re-scopes to the caller's own
 * portfolios (`investment_data/portfolio.py:1725`), and an advisor owns none.
 * That, not any filtering here, is why a desk with real clients showed an empty
 * book, no AUM, and a deep dive with every analytic tab blank: the client's
 * primary portfolio symbol comes off these rows, and without a symbol
 * `usePortfolio` is skipped.
 *
 * The `userId` ARGUMENT is honoured for any caller, so the book is assembled
 * per approved client instead — verified against the running backend: as the
 * advisor, `allUsers: true` → 0 rows, `userId: <client>` → that client's
 * portfolios with their market values. One query per client, which the firm's
 * own client list bounds; the cache serves repeats, so a screen that re-reads
 * the book does not re-ask.
 *
 * The listing alone is not enough, though. It answers with the symbol, the
 * owner and (sometimes) a market value, and leaves `totalInvestedAmount`,
 * `returnMetrics`, `holdings` and `historicalData` null — also verified against
 * the running backend. Every book figure that is not a sum of market values is
 * built from those: invested, P&L, return on cost, the scheme-level drivers the
 * Returns tab shows, the risk buckets, the value series. So each client
 * portfolio is then read once through `getPortfolio`, which does populate them
 * (GET_PORTFOLIO_BOOK_ROW, the book's own fields and nothing else).
 *
 * `useQuery` cannot loop, so this drives the client directly. The scope's ids
 * are joined into a key, which is what the effect depends on: the Set/array
 * identity from `useClientScope` changes on every render and would re-fetch for
 * ever.
 */

/**
 * How many portfolios are read in full for the roll-ups.
 *
 * One request each, so it is a real ceiling rather than a formality: a desk of
 * fifty clients with a goal or two apiece sits inside it, and the biggest
 * holdings — the ones the figures are made of — are read first. When it bites,
 * `enriched` on the book says how many rows are thin, so a screen can say the
 * numbers are partial instead of quietly under-reporting them.
 */
const DETAIL_CAP = 80;

/** Detail wins where it has a value; the listing keeps what detail left null. */
function mergeRow(listed, detail) {
  if (!detail) return listed;
  const out = { ...listed };
  Object.entries(detail).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    if (Array.isArray(value) && !value.length && Array.isArray(out[key]) && out[key].length) return;
    out[key] = value;
  });
  return out;
}
function useClientPortfolios({ withHistory }) {
  const apollo = useApolloClient();
  const scope = useClientScope();
  const key = scope.ids.join(',');
  const [state, setState] = useState({ loading: false, error: null, rows: EMPTY, key: null, enriched: 0, wanted: 0 });
  /* Bumped to force a re-read on refetch(), since the ids have not changed. */
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    if (scope.loading) return undefined;
    if (!key) { setState({ loading: false, error: null, rows: EMPTY, key: '', enriched: 0, wanted: 0 }); return undefined; }
    let current = true;
    setState((s) => ({ ...s, loading: true }));

    const listed = () => mapLimited(scope.ids, (id) => apollo
      .query({
        query: GET_PORTFOLIOS,
        variables: { userId: String(id), allUsers: false, limit: 200, includeHistorical: withHistory, historicalLimit: 40 },
        fetchPolicy: 'cache-first',
        errorPolicy: 'all',
      })
      .then((res) => res.data?.getPortfolios?.portfolios ?? EMPTY)
      /* One client's read failing must not empty the whole book. */
      .catch(() => EMPTY));

    /* The rows the figures are made of, biggest first, so a capped read keeps
       the money rather than whichever symbols sorted first. */
    const fill = (rows) => {
      const wanted = [...rows]
        .filter((p) => isClientBook(p))
        .sort(byDesc((p) => p.marketData?.marketValue))
        .slice(0, DETAIL_CAP);
      return mapLimited(wanted, (p) => apollo
        .query({
          query: GET_PORTFOLIO_BOOK_ROW,
          variables: { securitySymbol: p.securitySymbol, includeHistorical: withHistory, historicalLimit: 40 },
          fetchPolicy: 'cache-first',
          errorPolicy: 'all',
        })
        .then((res) => res.data?.getPortfolio?.portfolio ?? null)
        .catch(() => null))
        .then((details) => {
          const bySymbol = new Map(details.filter(Boolean).map((d) => [d.securitySymbol, d]));
          return {
            rows: rows.map((p) => mergeRow(p, bySymbol.get(p.securitySymbol))),
            enriched: bySymbol.size,
            wanted: wanted.length,
          };
        });
    };

    listed()
      .then((lists) => fill(lists.flat()))
      .then(({ rows, enriched, wanted }) => {
        if (!current) return;
        setState({ loading: false, error: null, rows, key, enriched, wanted });
      })
      .catch((err) => {
        if (!current) return;
        setState({ loading: false, error: err, rows: EMPTY, key, enriched: 0, wanted: 0 });
      });
    /* `current` is the only staleness guard, and the cleanup covers unmount as
       well as a re-run. A ref set once at mount would not: StrictMode invokes
       every effect twice, so a mount-only cleanup turns it off for good and
       every answer after that is thrown away — an empty book for ever. */
    return () => { current = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, scope.loading, withHistory, nonce, apollo]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);
  return {
    /* Loading until this read has answered for the ids currently in scope —
       otherwise a screen reads an empty book as "no clients". */
    loading: scope.loading || state.loading || (Boolean(key) && state.key !== key),
    error: scope.error || state.error,
    rows: state.rows,
    /** How many rows carry their full figures, and how many were asked for —
     *  equal unless DETAIL_CAP bit. */
    enriched: state.enriched,
    wanted: state.wanted,
    refetch,
  };
}

/**
 * The desk's book: every portfolio of every client who has approved sharing
 * with this firm, with the roll-ups each screen reads.
 *
 * `models` is empty for an advisor today — the firm's model portfolios are
 * SYSTEM-owned rows that only the `allUsers` path returns, and that path is
 * closed to them (see above). The model screens read `useAssetModels`
 * (`getAssetModelCompleteData`) for the catalogue, which is not user-scoped.
 */
export function useBook({ withHistory = false } = {}) {
  const scope = useClientScope();
  const sourced = useClientPortfolios({ withHistory });
  const { loading, error } = sourced;

  return useMemo(() => {
    /* Every row carries its headline risk resolved, because `riskMetrics` is a
       list of scenario runs: reading a field off it gives undefined, and a
       count built on that reports zero rather than failing. Attaching `risk`
       here means no caller has to know. */
    const all = sourced.rows.map((p) => ({
      ...p,
      risk: baseRiskRow(p.riskMetrics),
    }));
    /* Still filtered by the scope: the ids asked for are the scope's, and the
       server does not check them (`approved_client_ids` is wired to nothing),
       so a row for anyone else has no business in this firm's book. */
    const book = all.filter(isClientBook).filter((p) => scope.set.has(String(p.userId)));
    const models = all.filter((p) => !isClientBook(p));

    const aum = sum(book, (p) => p.marketData?.marketValue);
    const invested = sum(book, (p) => p.totalInvestedAmount);
    const pnl = sum(book, (p) => p.returnMetrics?.totalPnl);
    const unrealised = sum(book, (p) => p.returnMetrics?.unrealizedPnl);
    const clients = new Set(book.map((p) => p.userId).filter(Boolean));
    const sipMonthly = sum(book, (p) => p.goalData?.monthlyContribution);

    /* Book value series — every portfolio's snapshots aligned by date and summed. */
    const byDate = new Map();
    book.forEach((p) => {
      (p.historicalData ?? EMPTY).forEach((h) => {
        if (!h?.snapshotDate) return;
        byDate.set(h.snapshotDate, (byDate.get(h.snapshotDate) || 0) + (Number(h.marketValue) || 0));
      });
    });
    const valueSeries = [...byDate.entries()]
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([date, value]) => ({ date, value }));

    /* Month-on-month movement, used for the AUM delta chip. */
    const first = valueSeries[0]?.value;
    const last = valueSeries[valueSeries.length - 1]?.value ?? aum;
    const periodChangePct = first ? ((last - first) / first) * 100 : null;

    /* Drivers: which schemes are adding to / dragging on the book's P&L. */
    const byScheme = new Map();
    book.forEach((p) => {
      (p.holdings ?? EMPTY).forEach((h) => {
        const key = h.name || h.holdingSecuritySymbol;
        if (!key) return;
        const cur = byScheme.get(key) || {
          name: key, symbol: h.holdingSecuritySymbol, pnl: 0, value: 0, clients: new Set(),
        };
        cur.pnl += Number(h.unrealizedPnl) || 0;
        cur.value += Number(h.marketValue) || 0;
        if (p.userId) cur.clients.add(p.userId);
        byScheme.set(key, cur);
      });
    });
    const schemes = [...byScheme.values()].map((s) => ({ ...s, clients: s.clients.size }));
    const helping = [...schemes].sort(byDesc((s) => s.pnl)).filter((s) => s.pnl > 0).slice(0, 4);
    const hurting = [...schemes].sort((a, b) => a.pnl - b.pnl).filter((s) => s.pnl < 0).slice(0, 4);

    /* Risk buckets — each is a real count over the book, not a bucketed guess. */
    const vols = book.map((p) => Number(p.risk?.volatility)).filter(Number.isFinite);
    const medianVol = vols.length ? [...vols].sort((a, b) => a - b)[Math.floor(vols.length / 2)] : null;
    const concentrated = book.filter((p) => {
      const top = Math.max(0, ...(p.holdings ?? EMPTY).map((h) => Number(h.weight) || 0));
      return top > 0.25;
    });
    const drawdown = book.filter((p) => Number(p.risk?.maxDrawdown) < -0.15);
    const highVol = medianVol
      ? book.filter((p) => Number(p.risk?.volatility) > medianVol * 1.3)
      : EMPTY;
    const offTarget = book.filter((p) => Math.abs(Number(p.risk?.riskVsTarget) || 0) > 0.1);
    const underperforming = book.filter((p) => Number(p.returnMetrics?.oneYearReturn) < 0);

    /* AUM mix by portfolio type — the real composition of what's under advice.
       The stored types are lower-case (`model_portfolio`), so they are labelled
       rather than printed raw into the legend. */
    const mixMap = new Map();
    book.forEach((p) => {
      const key = label(p.portfolioType, 'Other');
      mixMap.set(key, (mixMap.get(key) || 0) + (Number(p.marketData?.marketValue) || 0));
    });
    const mix = [...mixMap.entries()]
      .map(([name, value]) => ({ label: name, value }))
      .sort((a, b) => b.value - a.value);

    return {
      loading,
      error,
      /** No firm yet, so no clients — told apart from "no clients" because a
       *  screen should offer Firm & team rather than an empty book. */
      noFirm: scope.noFirm,
      /** True when the detail cap bit, so the figures cover only the largest
       *  portfolios and a screen should say so rather than under-report. */
      partial: sourced.wanted > 0 && sourced.enriched < sourced.wanted,
      refetch: sourced.refetch,
      all,
      book,
      models,
      totals: {
        aum,
        invested,
        pnl,
        unrealised,
        clientCount: clients.size,
        portfolioCount: book.length,
        sipMonthly,
        periodChangePct,
        returnPct: invested ? (pnl / invested) * 100 : null,
      },
      valueSeries,
      helping,
      hurting,
      risk: {
        concentrated: concentrated.length,
        drawdown: drawdown.length,
        highVol: highVol.length,
        offTarget: offTarget.length,
        underperforming: underperforming.length,
        needsAction: new Set(
          [...concentrated, ...drawdown, ...offTarget, ...underperforming].map((p) => p.securitySymbol),
        ).size,
      },
      mix,
    };
  }, [sourced.rows, sourced.refetch, sourced.enriched, sourced.wanted, loading, error, scope]);
}

/**
 * The book's attention list — what the desk should act on this morning.
 *
 * Data only: an alert names where it leads as a route, and the screen turns
 * that into a button. Thresholds and wording live here so the Dashboard
 * renders a list instead of deciding what is worth saying, and so the same
 * list can feed a digest or a notification later without being re-derived.
 */
export function useBookAlerts({ book, clients, goals, plans }) {
  return useMemo(() => {
    const nameFor = (userId) =>
      clients.rows.find((c) => c.id === String(userId))?.name || `Client ${userId}`;
    const driftOf = (p) => Math.abs(Number(p.risk?.riskVsTarget) || 0);

    /* Off the policy risk target by more than 10 points, worst first. */
    const drifted = book.book.filter((p) => driftOf(p) > 0.1).sort(byDesc(driftOf));

    const alerts = [];

    if (drifted.length) {
      const p = drifted[0];
      alerts.push({
        id: `drift-${p.securitySymbol}`,
        icon: 'warn',
        title: `Allocation drift — ${nameFor(p.userId)}`,
        body: `${p.portfolioName || p.securitySymbol} is ${pct(driftOf(p) * 100)} away from its policy risk target. Rebalancing now restores the mandate; the Studio prices the tax impact before you send anything.`,
        time: p.latestDataDate ? daysAgo(p.latestDataDate) : null,
        actions: [
          { label: 'Generate rebalance proposal', to: `/proposals?portfolio=${p.securitySymbol}` },
          { label: 'Analyze first', to: `/analyzer?portfolio=${p.securitySymbol}`, variant: 'mute' },
        ],
      });
    }

    if (book.hurting.length) {
      const h = book.hurting[0];
      alerts.push({
        id: `detractor-${h.symbol || h.name}`,
        icon: 'view',
        title: `Detractor across the book — ${h.name}`,
        body: `${h.clients} ${h.clients === 1 ? 'client holds' : 'clients hold'} this scheme (${inr(h.value)} exposure) and it is ${inr(Math.abs(h.pnl))} underwater in aggregate. Research ranks the category alternates by alpha score.`,
        actions: [{ label: 'See alternates', to: '/research' }],
      });
    }

    if (plans.failing.length) {
      const names = plans.failing.map((s) => s.schemeName).filter(Boolean);
      alerts.push({
        id: 'sip-failures',
        icon: 'refresh',
        title: `SIP failures — ${plans.failing.length} ${plans.failing.length === 1 ? 'registration' : 'registrations'}`,
        body: `${names.slice(0, 3).join(', ')} ${names.length > 3 ? `and ${names.length - 3} more ` : ''}have consecutive debit failures. Mandate renewal links can go out in one batch.`,
        actions: [{ label: 'Open SIP book', to: '/transactions' }],
      });
    }

    goals.ahead.slice(0, 1).forEach((g) => {
      alerts.push({
        id: `goal-ahead-${g.goalId}`,
        icon: 'goals',
        title: `Goal ahead of schedule — ${g.displayName || g.goalName}`,
        body: `Now ${pct(g.fundedPct)} funded against plan. De-risking part of the corpus locks the win — a good agenda item for the next review call.`,
        actions: [{ label: 'Open goal plan', to: '/goals' }],
      });
    });

    goals.behind.slice(0, 1).forEach((g) => {
      alerts.push({
        id: `goal-behind-${g.goalId}`,
        icon: 'warn',
        title: `Goal behind plan — ${g.displayName || g.goalName}`,
        body: `Funded status ${g.fundedPct == null ? 'unavailable' : pct(g.fundedPct)}${g.probability != null ? `, success probability ${pct(g.probability)}` : ''}. A step-up or horizon change closes the gap.`,
        actions: [{ label: 'Fix this goal', to: '/goals' }],
      });
    });

    return { alerts, drifted, nameFor };
  }, [book.book, book.hurting, clients.rows, goals.ahead, goals.behind, plans.failing]);
}
